/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   Thu Jun 6 2019 3:16:54 PM
 *
 *******************************************************/
import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, DisplayText, FormLayout, Card, Layout } from '@shopify/polaris';
import { Frame } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Field, reduxForm } from 'redux-form';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { routes } from 'src/routes';
import { StepProgress } from 'src/components/views/installation/step-progress';

import history from 'src/routes/history';
import * as installActions from 'actions/install-actions';
import {
  WHO_YOU_ARE_OPTIONS,
  INDUSTRY_OPTIONS,
  SOURCE_FROM_OPTIONS
} from 'src/constants/demographic';
import { isLocalStorageAvailable } from 'src/helpers';

type Props = WithI18nProps;

const DEMO_GRAPHIC_KEY = 'demographicKey';
const SOURCE_FROM_VALUE_OTHER = 'Other';
class Demographic extends Component<Props> {
  state = {
    isSourceFromSelectOther: false
  };

  componentDidMount() {
    try {
      const demographic = localStorage.getItem(DEMO_GRAPHIC_KEY);

      if (demographic && demographic !== 'undefined') {
        const { sourceFrom } = JSON.parse(demographic);
        this.setState({
          sourceFrom
        });
      }
    } catch (error) {}
  }

  render() {
    const { i18n, handleSubmit, loading } = this.props;
    const { isSourceFromSelectOther, sourceFrom } = this.state;

    /**
     * transform options
     */
    const whoYouAreOptions = WHO_YOU_ARE_OPTIONS.map(option => ({
      ...option,
      label: i18n.translate(option.label)
    }));
    const industryOptions = INDUSTRY_OPTIONS.map(option => ({
      ...option,
      label: i18n.translate(option.label)
    }));
    const sourceFromOptions = SOURCE_FROM_OPTIONS.map(option => ({
      ...option,
      label: i18n.translate(option.label)
    }));

    return (
      <div className="BC-AnotherLayout BC-page_Demographic">
        <Frame>
          <div className="Polaris-Page">
            <div className="Polaris-Page-Header">
              <DisplayText size="extraLarge">
                {i18n.translate('AdminHTML.Installation.Demographic.displayText1')}
              </DisplayText>
            </div>
          </div>

          <Page
            title={i18n.translate('AdminHTML.Installation.Demographic.pageTitle')}
            primaryAction={
              <div className="step-progress-initializing">
                <StepProgress step={1} />
              </div>
            }>
            <Layout>
              <Layout.Section>
                <Card
                  sectioned
                  title={i18n.translate('AdminHTML.Installation.Demographic.cardTitle')}
                  primaryFooterAction={{
                    content: i18n.translate('AdminHTML.Common.Action.next'),
                    onAction: handleSubmit(this.handleSave.bind(this)),
                    loading
                  }}>
                  <FormLayout>
                    <Field
                      name="picInstall"
                      label={i18n.translate('AdminHTML.Installation.Demographic.select1_Title')}
                      placeholder={i18n.translate(
                        'AdminHTML.Installation.Demographic.select1_Placeholder'
                      )}
                      component={RenderSelect}
                      options={whoYouAreOptions}
                    />
                    <Field
                      name="industry"
                      label={i18n.translate('AdminHTML.Installation.Demographic.select2_Title')}
                      placeholder={i18n.translate(
                        'AdminHTML.Installation.Demographic.select2_Placeholder'
                      )}
                      component={RenderSelect}
                      options={industryOptions}
                    />
                    <Field
                      name="sourceFrom"
                      label={i18n.translate('AdminHTML.Installation.Demographic.select3_Title')}
                      placeholder={i18n.translate(
                        'AdminHTML.Installation.Demographic.select3_Placeholder'
                      )}
                      component={RenderSelect}
                      options={sourceFromOptions}
                      onChange={value => this.onSelectSourceFrom(value)}
                    />

                    {(isSourceFromSelectOther || sourceFrom === SOURCE_FROM_VALUE_OTHER) && (
                      <Field
                        name="sourceOther"
                        label={i18n.translate('AdminHTML.Installation.Common.yourMessage')}
                        placeholder={i18n.translate(
                          'AdminHTML.Installation.Demographic.select3_Title'
                        )}
                        component={RenderTextField}
                        anotherProps={{ multiline: 5 }}
                      />
                    )}
                  </FormLayout>
                </Card>
              </Layout.Section>
            </Layout>
          </Page>
        </Frame>
      </div>
    );
  }

  saveChoseToLocalStore(addDemographic) {
    try {
      localStorage.setItem(DEMO_GRAPHIC_KEY, JSON.stringify(addDemographic));
    } catch (error) {}
  }

  /**
   * handle click button next
   * @param {*} data
   */
  handleSave = async data => {
    const demographicForm = {
      picInstall: _.get(data, 'picInstall', ''),
      industry: _.get(data, 'industry', ''),
      sourceFrom: _.get(data, 'sourceFrom', ''),
      sourceOther:
        _.get(data, 'sourceFrom', '') === SOURCE_FROM_VALUE_OTHER
          ? _.get(data, 'sourceOther', '')
          : ''
    };
    const { addDemographic, updateInstallationStatus } = this.props;

    this.saveChoseToLocalStore(demographicForm);

    /**
     * sync demographic info to AC
     */
    await addDemographic(demographicForm);

    /**
     * update tenant app install statusnpm
     */
    await updateInstallationStatus('submitted-demographic');

    /**
     * Save temp data into localStorage
     */
    this.saveChoseToLocalStore(demographicForm);
    history.push(routes.chooseLayout.view());
  };

  /**
   * handle on change source from field
   * @param {string} value
   */
  onSelectSourceFrom = value => {
    // Other value of option Other
    if (value === SOURCE_FROM_VALUE_OTHER) {
      this.setState({ sourceFrom: value, isSourceFromSelectOther: true });
    } else {
      this.setState({ sourceFrom: value, isSourceFromSelectOther: false });
    }
  };
}

const mapStateToProps = (state, props) => ({
  loading: state.install.loading,
  success: state.install.success,
  error: state.install.error
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  addDemographic: body => dispatch(installActions.addDemographic(body)),
  updateInstallationStatus: status => dispatch(installActions.updateInstallationStatus(status))
});

Demographic = reduxForm({
  form: 'DemographicForm',
  enableReinitialize: true
})(Demographic);

Demographic = connect(function () {
  const demographic =
    isLocalStorageAvailable() &&
    localStorage.getItem(DEMO_GRAPHIC_KEY) &&
    localStorage.getItem(DEMO_GRAPHIC_KEY) !== 'undefined'
      ? localStorage.getItem(DEMO_GRAPHIC_KEY)
      : '{}';
  return {
    initialValues: {
      industry: JSON.parse(demographic)?.industry,
      picInstall: JSON.parse(demographic)?.picInstall,
      sourceFrom: JSON.parse(demographic)?.sourceFrom,
      sourceOther: JSON.parse(demographic)?.sourceOther
    }
  };
})(Demographic);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Demographic));
